import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { sportConfig } from "@/lib/coachConstants";
import type { CompletedActivity } from "@/types";

interface SportDistributionChartProps {
  activities: CompletedActivity[];
}

interface SportSlice {
  sport: string;
  label: string;
  minutes: number;
  color: string;
}

export function SportDistributionChart({ activities }: SportDistributionChartProps) {
  const slices = useMemo(() => {
    const sportMinutes = new Map<string, number>();
    for (const a of activities) {
      const sport = a.sport ?? "other";
      const mins = (a.duration ?? 0) / 60;
      sportMinutes.set(sport, (sportMinutes.get(sport) ?? 0) + mins);
    }

    const result: SportSlice[] = [];
    for (const [sport, minutes] of sportMinutes) {
      const cfg = sportConfig[sport as keyof typeof sportConfig];
      result.push({
        sport,
        label: cfg?.label ?? sport,
        minutes: Math.round(minutes),
        color: cfg?.color ?? "hsl(220 10% 50%)",
      });
    }
    return result.sort((a, b) => b.minutes - a.minutes);
  }, [activities]);

  const totalMinutes = slices.reduce((sum, s) => sum + s.minutes, 0);

  if (!slices.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Sport Distribution</h3>
        <div className="h-[240px] flex items-center justify-center text-muted-foreground text-sm">
          No activity data to show sport distribution.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">Sport Distribution</h3>
      <div className="flex items-center gap-4">
        <div className="h-[180px] w-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="minutes"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={2}
                strokeWidth={0}
              >
                {slices.map((s) => (
                  <Cell key={s.sport} fill={s.color} fillOpacity={0.7} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }}
                formatter={(value: number) => [`${value} min`, ""]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 space-y-1.5">
          {slices.map((s) => {
            const pct = totalMinutes > 0 ? ((s.minutes / totalMinutes) * 100).toFixed(0) : "0";
            return (
              <div key={s.sport} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color, opacity: 0.7 }} />
                <span className="text-xs text-foreground flex-1">{s.label}</span>
                <span className="text-[10px] font-mono tabular-nums text-muted-foreground">{pct}%</span>
                <span className="text-[10px] font-mono tabular-nums text-foreground w-14 text-right">
                  {s.minutes > 60 ? `${(s.minutes / 60).toFixed(1)}h` : `${s.minutes}m`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
